import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Loader2, Send, Save, Clock, Trash2, Paperclip, Bold, Italic, List, Link2, Pencil, Users2, XCircle, BarChart3, MousePointerClick, Mail, Eye, Layout, Image as ImageIcon, Smartphone, Monitor, Video, CalendarCheck, Download, Copy } from "lucide-react";

const LOGO_OPTS = [["light", "Logo bianco (per sfondi scuri)"], ["dark", "Logo scuro (per sfondi chiari)"], ["mark", "Solo simbolo"], ["none", "Nessun logo"]];

const CATS = ["A", "B", "C", "D"];
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABEL = { draft: "Bozza", scheduled: "Programmata", sending: "In invio", sent: "Inviata" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT") : "—");

export default function Newsletter() {
  const [subject, setSubject] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const DEF_H = { logo: "light", text: "", bg: "#111111" };
  const DEF_F = { logo: "light", address: "", copyright: "", privacy: "", show_unsub: true };
  const [header, setHeader] = useState(DEF_H);
  const [footer, setFooter] = useState(DEF_F);
  const [hfDefaults, setHfDefaults] = useState(null);
  const [showHF, setShowHF] = useState(false);
  const [audience, setAudience] = useState({ partners: false, clients: false, agents: false, prospects: false, prospect_regions: [], contact_categories: [], partner_categories: [], partner_regions: [] });
  const [attach, setAttach] = useState(null); // {id, filename}
  const [imgTop, setImgTop] = useState(null);
  const [bottomImgs, setBottomImgs] = useState([]);
  const [mediaGrid, setMediaGrid] = useState({ columns: 2, desc_pos: "below", items: [] });
  const DEF_RSVP = { enabled: false, title: "", event_date: "", location: "", description: "", ask_guests: true, deadline: "", event_id: "" };
  const [rsvp, setRsvp] = useState(DEF_RSVP);
  const DEF_POLL = { enabled: false, question: "", options: ["", ""] };
  const [poll, setPoll] = useState(DEF_POLL);
  const [eventOpts, setEventOpts] = useState([]);
  useEffect(() => { api.get(`/eventi/select`).then((r) => setEventOpts(r.data || [])).catch(() => setEventOpts([])); }, []);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewMode, setPreviewMode] = useState("both");
  const [editingId, setEditingId] = useState(null);
  const [count, setCount] = useState(null);
  const [audStats, setAudStats] = useState(null);
  const [busy, setBusy] = useState("");
  const [schedAt, setSchedAt] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [regions, setRegions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [resending, setResending] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [audList, setAudList] = useState(null);
  const [listBusy, setListBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const editorRef = useRef(null);
  const [editorKey, setEditorKey] = useState("new");

  const loadCampaigns = () => api.get("/newsletter/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  const dupCampaign = async (id) => {
    try { await api.post(`/newsletter/campaigns/${id}/duplicate`); toast.success("Campagna duplicata come bozza"); loadCampaigns(); }
    catch { toast.error("Errore nella duplicazione"); }
  };
  const loadCategories = () => api.get("/newsletter/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  useEffect(() => {
    loadCampaigns(); loadCategories();
    api.get("/regions").then((r) => setRegions(r.data || [])).catch(() => {});
    api.get("/newsletter/hf-defaults").then((r) => { setHfDefaults(r.data); setHeader(r.data.header); setFooter(r.data.footer); }).catch(() => {});
  }, []);

  const audQuery = useCallback(() => `?partners=${audience.partners}&clients=${audience.clients}&agents=${audience.agents}&prospects=${audience.prospects}&prospect_regions=${encodeURIComponent(audience.prospect_regions.join(","))}&contact_categories=${encodeURIComponent(audience.contact_categories.join(","))}&categories=${audience.partner_categories.join(",")}&regions=${encodeURIComponent(audience.partner_regions.join(","))}`, [audience]);

  const refreshCount = useCallback(() => {
    if (!audience.partners && !audience.clients && !audience.agents && !audience.prospects && audience.contact_categories.length === 0) { setCount(0); setAudStats(null); return; }
    api.get(`/newsletter/audience-count${audQuery()}`).then((r) => { setCount(r.data.count); setAudStats(r.data); }).catch(() => { setCount(null); setAudStats(null); });
  }, [audience, audQuery]);
  useEffect(() => { refreshCount(); setAudList(null); }, [refreshCount]);

  const loadAudienceList = async () => {
    if (audList) { setAudList(null); return; }
    setListBusy(true);
    try { const r = await api.get(`/newsletter/audience-list${audQuery()}`); setAudList(r.data.recipients || []); }
    catch { toast.error("Impossibile caricare l'elenco"); }
    finally { setListBusy(false); }
  };

  const getHtml = () => editorRef.current?.innerHTML || "";
  const cmd = (c, v = null) => { document.execCommand(c, false, v); editorRef.current?.focus(); };
  const addLink = () => { const u = window.prompt("URL (https://...)"); if (u) cmd("createLink", u); };
  const onPaste = (e) => {
    const html = e.clipboardData.getData("text/html");
    if (!html) return;
    e.preventDefault();
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("*").forEach((el) => {
      el.removeAttribute("bgcolor"); el.removeAttribute("color"); el.removeAttribute("class");
      if (el.style) { el.style.color = ""; el.style.backgroundColor = ""; el.style.background = ""; }
    });
    doc.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((h) => { const p = doc.createElement("p"); p.innerHTML = h.innerHTML; h.replaceWith(p); });
    doc.querySelectorAll("b,strong").forEach((b) => { if (/^(normal|400)$/.test(b.style.fontWeight)) b.replaceWith(...b.childNodes); });
    document.execCommand("insertHTML", false, doc.body.innerHTML);
  };

  const toggleCat = (c) => setAudience((a) => ({ ...a, partner_categories: a.partner_categories.includes(c) ? a.partner_categories.filter((x) => x !== c) : [...a.partner_categories, c] }));
  const toggleReg = (r) => setAudience((a) => ({ ...a, partner_regions: a.partner_regions.includes(r) ? a.partner_regions.filter((x) => x !== r) : [...a.partner_regions, r] }));

  const resetForm = () => {
    setEditingId(null); setSubject(""); setFromName(""); setReplyTo(""); setAttach(null); setImgTop(null); setBottomImgs([]); setSchedAt("");
    setMediaGrid({ columns: 2, desc_pos: "below", items: [] });
    setRsvp(DEF_RSVP); setPoll(DEF_POLL);
    setHeader(hfDefaults?.header || DEF_H); setFooter(hfDefaults?.footer || DEF_F);
    setAudience({ partners: false, clients: false, agents: false, prospects: false, prospect_regions: [], contact_categories: [], partner_categories: [], partner_regions: [] });
    setEditorKey("new-" + Date.now());
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Solo file PDF"); return; }
    const fd = new FormData(); fd.append("file", f);
    setBusy("upload");
    try { const r = await api.post("/newsletter/assets", fd, { headers: { "Content-Type": "multipart/form-data" } }); setAttach(r.data); toast.success("PDF allegato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const uploadImage = async (e, pos) => {    const f = e.target.files?.[0]; if (!f) return;
    const vid = f.type.startsWith("video/");
    if (!f.type.startsWith("image/") && !vid) { toast.error("Solo immagini (PNG/JPG) o video (MP4/MOV)"); e.target.value = ""; return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("File troppo grande (max 50MB)"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    setBusy("img-" + pos);
    if (vid) toast.info("Trasformo il video in GIF animata…");
    try {
      const r = await api.post(vid ? "/newsletter/assets/video-gif" : "/newsletter/assets", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 });
      if (vid) toast.success(`GIF animata pronta (${(r.data.size / 1048576).toFixed(1)} MB): parte da sola all'apertura`);
      if (pos === "top") setImgTop(r.data);
      else setBottomImgs((a) => (a.length >= 3 ? a : [...a, r.data]));
      if (!vid) toast.success("Immagine caricata");
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const uploadMedia = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const isImg = f.type.startsWith("image/"); const isVid = f.type.startsWith("video/");
    if (!isImg && !isVid) { toast.error("Solo immagini o video"); e.target.value = ""; return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("File troppo grande (max 50MB)"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    setBusy("media");
    try {
      const r = await api.post("/newsletter/assets", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setMediaGrid((g) => (g.items.length >= 9 ? g : { ...g, items: [...g.items, { id: r.data.id, kind: isVid ? "video" : "image", filename: r.data.filename, desc: "", link: "", poster_id: null }] }));
      toast.success(isVid ? "Video caricato" : "Immagine caricata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const uploadPoster = async (e, idx) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("La copertina deve essere un'immagine"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    setBusy("poster-" + idx);
    try {
      const r = await api.post("/newsletter/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMediaGrid((g) => ({ ...g, items: g.items.map((it, i) => (i === idx ? { ...it, poster_id: r.data.id } : it)) }));
      toast.success("Copertina caricata");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload fallito"); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const setMediaItem = (idx, patch) => setMediaGrid((g) => ({ ...g, items: g.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const removeMediaItem = (idx) => setMediaGrid((g) => ({ ...g, items: g.items.filter((_, i) => i !== idx) }));

  const openPreview = async () => {
    setPreviewing(true);
    try { const r = await api.post("/newsletter/preview", payload("draft"), { timeout: 60000 }); setPreview(r.data.html || ""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Anteprima non riuscita"); }
    finally { setPreviewing(false); }
  };

  const payload = (action) => ({
    action, subject, from_name: fromName, reply_to: replyTo, header, footer, html: getHtml(), audience, attachment_id: attach?.id || null, attachment_name: attach?.filename || null,
    image_top_id: imgTop?.id || null, image_top_name: imgTop?.filename || null,
    images_bottom: bottomImgs.map((x) => ({ id: x.id, name: x.filename })),
    media_grid: { columns: mediaGrid.columns, desc_pos: mediaGrid.desc_pos, items: mediaGrid.items.map((it) => ({ id: it.id, kind: it.kind, name: it.filename, desc: it.desc, link: it.link, poster_id: it.poster_id })) },
    rsvp,
    poll: { enabled: poll.enabled, question: poll.question, options: poll.options.map((o) => o.trim()).filter(Boolean) },
    public_base: PUBLIC_BASE, scheduled_at: action === "schedule" && schedAt ? new Date(schedAt).toISOString() : null,
  });

  const submit = async (action) => {
    if (!subject.trim()) { toast.error("Inserisci un oggetto"); return; }
    if (action === "send" && (count === 0)) { toast.error("Nessun destinatario selezionato"); return; }
    if (action === "schedule" && !schedAt) { toast.error("Scegli data e ora"); return; }
    setBusy(action);
    try {
      const p = payload(action);
      const res = editingId ? await api.put(`/newsletter/campaigns/${editingId}`, p) : await api.post("/newsletter/campaigns", p);
      toast.success(action === "send" ? "Invio avviato" : action === "schedule" ? "Newsletter programmata" : "Bozza salvata");
      if (action !== "draft") resetForm(); else setEditingId(res.data.id);
      loadCampaigns();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(""); }
  };

  const testSend = async () => {
    setBusy("test");
    try {
      // salva/aggiorna prima per avere un id con contenuto corrente
      const p = payload("draft");
      const res = editingId ? await api.put(`/newsletter/campaigns/${editingId}`, p) : await api.post("/newsletter/campaigns", p);
      const id = editingId || res.data.id; setEditingId(id);
      const t = await api.post(`/newsletter/campaigns/${id}/test`);
      toast.success(`Prova inviata a ${t.data.to}`); loadCampaigns();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(""); }
  };

  const editCampaign = (c) => {
    setEditingId(c.id); setSubject(c.subject || ""); setFromName(c.from_name || ""); setReplyTo(c.reply_to || "");
    setHeader({ ...(hfDefaults?.header || DEF_H), ...(c.header || {}) });
    setFooter({ ...(hfDefaults?.footer || DEF_F), ...(c.footer || {}) });
    const a = c.audience || {};
    const cc = [...(a.contact_categories || [])];
    if (a.mondo_torri) cc.push("mondo_torri");
    if (a.iscritti_sito) cc.push("sito");
    if (a.contacts) cc.push("generici");
    setAudience({ partners: !!a.partners, clients: !!a.clients, agents: !!a.agents, prospects: !!a.prospects, prospect_regions: a.prospect_regions || [], contact_categories: [...new Set(cc)], partner_categories: a.partner_categories || [], partner_regions: a.partner_regions || [] });
    setAttach(c.attachment_id ? { id: c.attachment_id, filename: c.attachment_name || "allegato.pdf" } : null);
    setImgTop(c.image_top_id ? { id: c.image_top_id, filename: c.image_top_name || "immagine" } : null);
    setBottomImgs(c.images_bottom?.length ? c.images_bottom.map((x) => ({ id: x.id, filename: x.name || "immagine" })) : (c.image_bottom_id ? [{ id: c.image_bottom_id, filename: c.image_bottom_name || "immagine" }] : []));
    setMediaGrid({ columns: [1, 2, 3].includes(c.media_grid?.columns) ? c.media_grid.columns : 2, desc_pos: c.media_grid?.desc_pos === "side" ? "side" : "below", items: (c.media_grid?.items || []).map((it) => ({ id: it.id, kind: it.kind === "video" ? "video" : "image", filename: it.name || "media", desc: it.desc || "", link: it.link || "", poster_id: it.poster_id || null })) });
    setRsvp({ ...DEF_RSVP, ...(c.rsvp || {}) });
    setPoll({ enabled: !!c.poll?.enabled, question: c.poll?.question || "", options: (c.poll?.options?.length ? c.poll.options : ["", ""]).slice(0, 5) });
    setSchedAt(""); setEditorKey("edit-" + c.id);
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = c.html || ""; }, 0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openDetail = async (c) => {
    setDetail({ loading: true, subject: c.subject }); setDetailBusy(true); setDetailId(c.id);
    try { const r = await api.get(`/newsletter/campaigns/${c.id}/detail`); setDetail(r.data); }
    catch (err) { toast.error("Impossibile caricare il dettaglio"); setDetail(null); }
    finally { setDetailBusy(false); }
  };

  const resendFailed = async () => {
    if (!detailId) return;
    setResending(true);
    try {
      const r = await api.post(`/newsletter/campaigns/${detailId}/resend-failed`);
      if (!r.data.count) { toast.info("Nessun invio fallito da reinviare."); return; }
      toast.success(`Reinvio avviato per ${r.data.count} destinatari falliti. Ricontrolla il dettaglio tra poco.`);
      setTimeout(async () => {
        try { const d = await api.get(`/newsletter/campaigns/${detailId}/detail`); setDetail(d.data); loadCampaigns(); } catch { /* ignore */ }
      }, 6000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reinvio non riuscito"); }
    finally { setResending(false); }
  };

  const delCampaign = async (id) => { if (!window.confirm("Eliminare la campagna?")) return; await api.delete(`/newsletter/campaigns/${id}`); if (editingId === id) resetForm(); loadCampaigns(); };

  const exportRsvp = async (id) => {
    try {
      const r = await api.get(`/eventi/${id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `rsvp-${id}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export non riuscito"); }
  };

  const exportPoll = async (id) => {
    try {
      const r = await api.get(`/newsletter/campaigns/${id}/poll/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `sondaggio-${id}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export non riuscito"); }
  };

  const audienceOn = audience.partners || audience.clients || audience.agents || audience.prospects || audience.contact_categories.length > 0;

  return (
    <div className="space-y-6" data-testid="newsletter-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tight"><Megaphone className="h-7 w-7 text-primary" /> Newsletter</h1>
        <p className="text-sm text-muted-foreground">Invia comunicazioni a partner, agenti e contatti extra. Bozze, programmazione e disiscrizione inclusi.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Composer */}
        <Card className="space-y-4 p-5 lg:col-span-2" data-testid="composer-card">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold">{editingId ? "Modifica campagna" : "Nuova campagna"}</h2>
            {editingId && <Button variant="ghost" size="sm" onClick={resetForm} data-testid="new-campaign-btn">Nuova</Button>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Oggetto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto della newsletter" data-testid="nl-subject" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Mittente (nome visualizzato)</Label>
              <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Es. Torri Solar — Rete Vendita" data-testid="nl-from-name" />
              <p className="text-[10px] text-muted-foreground">Vuoto = usa il mittente predefinito delle impostazioni email.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email a cui rispondere (Reply-To)</Label>
              <Input type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="Es. commerciale@torrisolare.it" data-testid="nl-reply-to" />
              <p className="text-[10px] text-muted-foreground">Vuoto = usa il Reply-To predefinito.</p>
            </div>
          </div>

          {/* Header/Footer builder */}
          <div className="rounded-lg border border-input bg-muted/20" data-testid="hf-section">
            <button type="button" onClick={() => setShowHF((v) => !v)} className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold" data-testid="hf-toggle">
              <span className="flex items-center gap-2"><Layout className="h-4 w-4 text-primary" /> Intestazione e piè di pagina</span>
              <span className="text-xs text-muted-foreground">{showHF ? "Nascondi" : "Personalizza"}</span>
            </button>
            {showHF && (
              <div className="space-y-5 border-t border-input px-4 py-4">
                {/* HEADER */}
                <div className="space-y-3">
                  <div className="text-xs font-bold uppercase tracking-wide text-primary">Intestazione</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Logo</Label>
                      <select value={header.logo} onChange={(e) => setHeader((h) => ({ ...h, logo: e.target.value }))} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="hf-header-logo">
                        {LOGO_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Colore sfondo intestazione</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={header.bg} onChange={(e) => setHeader((h) => ({ ...h, bg: e.target.value }))} className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background" data-testid="hf-header-bg" />
                        <Input value={header.bg} onChange={(e) => setHeader((h) => ({ ...h, bg: e.target.value }))} className="h-9 flex-1 font-mono text-xs" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Testo sotto il logo (opzionale)</Label>
                    <Input value={header.text} onChange={(e) => setHeader((h) => ({ ...h, text: e.target.value }))} placeholder="Es. Lettera alla Rete n.12" data-testid="hf-header-text" />
                  </div>
                </div>
                {/* FOOTER */}
                <div className="space-y-3 border-t border-input pt-4">
                  <div className="text-xs font-bold uppercase tracking-wide text-primary">Piè di pagina</div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Logo</Label>
                    <select value={footer.logo} onChange={(e) => setFooter((f) => ({ ...f, logo: e.target.value }))} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid="hf-footer-logo">
                      {LOGO_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Indirizzo</Label>
                      <Input value={footer.address} onChange={(e) => setFooter((f) => ({ ...f, address: e.target.value }))} placeholder="Brescia, Italia" data-testid="hf-footer-address" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Copyright</Label>
                      <Input value={footer.copyright} onChange={(e) => setFooter((f) => ({ ...f, copyright: e.target.value }))} placeholder="© 2026 Torri Solare…" data-testid="hf-footer-copyright" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Testo privacy / disclaimer</Label>
                    <Textarea value={footer.privacy} onChange={(e) => setFooter((f) => ({ ...f, privacy: e.target.value }))} rows={4} className="text-xs" data-testid="hf-footer-privacy" />
                  </div>
                  <label className="flex items-center justify-between text-sm">
                    <span>Mostra link di cancellazione/disiscrizione</span>
                    <Switch checked={footer.show_unsub} onCheckedChange={(v) => setFooter((f) => ({ ...f, show_unsub: v }))} data-testid="hf-footer-unsub" />
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Contenuto</Label>
            {/* Image before content */}
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-input bg-muted/20 p-2" data-testid="img-top-block">
              <input id="img-top-input" type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadImage(e, "top")} data-testid="nl-img-top-input" />
              {imgTop ? (
                <span className="flex items-center gap-2">
                  <img src={PUBLIC_BASE + "/api/newsletter/asset/" + imgTop.id} alt="top" className="h-12 w-auto rounded border border-white/10 object-cover" data-testid="img-top-preview" />
                  <button onClick={() => setImgTop(null)} className="text-red-400" data-testid="img-top-remove"><Trash2 className="h-4 w-4" /></button>
                </span>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("img-top-input").click()} disabled={busy === "img-top"} data-testid="nl-img-top-btn">
                  {busy === "img-top" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />} Immagine in alto
                </Button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground" data-testid="img-format-hint">Consigliato: immagini orizzontali o quadrate (larghezza ≥ 1200px). Le immagini molto verticali (es. screenshot da telefono) verranno automaticamente ridotte e centrate per non occupare troppo spazio nell'email. Puoi caricare anche un <b>video MP4/MOV</b>: diventa una GIF animata in loop (primi 7 secondi, 600 px) che parte da sola all'apertura dell'email.</p>
            <div className="flex flex-wrap gap-1 rounded-t-md border border-b-0 border-input bg-muted/40 p-1">
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("bold")} data-testid="fmt-bold"><Bold className="h-4 w-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("italic")} data-testid="fmt-italic"><Italic className="h-4 w-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => cmd("insertUnorderedList")} data-testid="fmt-list"><List className="h-4 w-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={addLink} data-testid="fmt-link"><Link2 className="h-4 w-4" /></Button>
            </div>
            <div
              key={editorKey}
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              data-testid="nl-editor"
              onPaste={onPaste}
              className="min-h-[220px] rounded-b-md border border-input bg-background p-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5"
            />
            {/* Images after content (max 3) */}
            <div className="mt-2 space-y-2 rounded-md border border-dashed border-input bg-muted/20 p-2" data-testid="img-bottom-block">
              <input id="img-bottom-input" type="file" accept="image/*,video/*" className="hidden" onChange={(e) => uploadImage(e, "bottom")} data-testid="nl-img-bottom-input" />
              <div className="flex flex-wrap items-center gap-2">
                {bottomImgs.map((im, i) => (
                  <span key={im.id} className="relative" data-testid={`img-bottom-preview-${i}`}>
                    <img src={PUBLIC_BASE + "/api/newsletter/asset/" + im.id} alt={`foto ${i + 1}`} className="h-14 w-14 rounded border border-white/10 object-cover" />
                    <button onClick={() => setBottomImgs((a) => a.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-500 p-0.5 text-white" data-testid={`img-bottom-remove-${i}`}><XCircle className="h-3.5 w-3.5" /></button>
                  </span>
                ))}
                {bottomImgs.length < 3 && (
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("img-bottom-input").click()} disabled={busy === "img-bottom"} data-testid="nl-img-bottom-btn">
                    {busy === "img-bottom" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageIcon className="mr-2 h-4 w-4" />} {bottomImgs.length ? "Aggiungi foto" : "Immagine/i in basso"}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">Carosello foto sotto il testo — massimo 3 immagini (affiancate nell'email). Usa preferibilmente immagini orizzontali/quadrate: quelle molto verticali verranno ridotte e centrate.</p>
            </div>
            {/* Griglia media: foto e video con descrizione */}
            <div className="mt-2 space-y-3 rounded-md border border-dashed border-input bg-muted/20 p-3" data-testid="media-grid-block">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold">Griglia media (foto e video)</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">Colonne</span>
                    {[1, 2, 3].map((n) => (
                      <button key={n} type="button" onClick={() => setMediaGrid((g) => ({ ...g, columns: n }))}
                        className={`h-6 w-6 rounded text-xs font-semibold transition-colors ${mediaGrid.columns === n ? "bg-primary text-primary-foreground" : "border border-input text-muted-foreground hover:text-foreground"}`}
                        data-testid={`media-cols-${n}`}>{n}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">Descrizione</span>
                    {[["below", "Sotto"], ["side", "A lato"]].map(([v, lbl]) => (
                      <button key={v} type="button" onClick={() => setMediaGrid((g) => ({ ...g, desc_pos: v }))}
                        className={`rounded px-2 py-1 text-[10px] font-semibold transition-colors ${mediaGrid.desc_pos === v ? "bg-primary text-primary-foreground" : "border border-input text-muted-foreground hover:text-foreground"}`}
                        data-testid={`media-descpos-${v}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              </div>
              {mediaGrid.items.length > 0 && (
                <div className="space-y-2">
                  {mediaGrid.items.map((it, i) => (
                    <div key={it.id} className="flex flex-wrap items-start gap-3 rounded-md border border-white/10 bg-background/40 p-2" data-testid={`media-item-${i}`}>
                      {it.kind === "video" ? (
                        <div className="flex flex-col items-center gap-1">
                          {it.poster_id ? (
                            <img src={PUBLIC_BASE + "/api/newsletter/asset/" + it.poster_id} alt="poster" className="h-16 w-24 rounded border border-white/10 object-cover" />
                          ) : (
                            <div className="flex h-16 w-24 items-center justify-center rounded border border-white/10 bg-black text-primary"><Video className="h-6 w-6" /></div>
                          )}
                          <input id={`poster-input-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPoster(e, i)} />
                          <button type="button" onClick={() => document.getElementById(`poster-input-${i}`).click()} disabled={busy === "poster-" + i} className="text-[10px] text-primary underline" data-testid={`media-poster-btn-${i}`}>{it.poster_id ? "Cambia copertina" : "Aggiungi copertina"}</button>
                        </div>
                      ) : (
                        <img src={PUBLIC_BASE + "/api/newsletter/asset/" + it.id} alt="media" className="h-16 w-24 rounded border border-white/10 object-cover" />
                      )}
                      <div className="min-w-[200px] flex-1 space-y-1">
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">{it.kind === "video" ? <><Video className="h-3 w-3" /> Video</> : <><ImageIcon className="h-3 w-3" /> Foto</>} · {it.filename}</span>
                        <Textarea value={it.desc} onChange={(e) => setMediaItem(i, { desc: e.target.value })} placeholder="Descrizione…" className="min-h-[52px] text-xs" data-testid={`media-desc-${i}`} />
                        {it.kind === "image" && <Input value={it.link} onChange={(e) => setMediaItem(i, { link: e.target.value })} placeholder="Link (opzionale, https://…)" className="h-7 text-xs" data-testid={`media-link-${i}`} />}
                      </div>
                      <button type="button" onClick={() => removeMediaItem(i)} className="text-red-400" data-testid={`media-remove-${i}`}><XCircle className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              )}
              {mediaGrid.items.length < 9 && (
                <>
                  <input id="media-input" type="file" accept="image/*,video/*" className="hidden" onChange={uploadMedia} data-testid="nl-media-input" />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("media-input").click()} disabled={busy === "media"} data-testid="nl-media-btn">
                    {busy === "media" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Video className="mr-2 h-4 w-4" />} Aggiungi foto o video
                  </Button>
                </>
              )}
              <p className="text-[10px] text-muted-foreground">Ogni riquadro mostra un media con la sua descrizione. I <b>video</b> non partono dentro l'email: viene mostrata una copertina con ▶ che apre il video al clic (carica una copertina per un risultato migliore). Su telefono le colonne restano affiancate.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input id="pdf-input" type="file" accept="application/pdf" className="hidden" onChange={uploadPdf} data-testid="nl-pdf-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("pdf-input").click()} disabled={busy === "upload"} data-testid="nl-attach-btn">
              {busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />} Allega PDF
            </Button>
            {attach && <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="attach-name">{attach.filename}<button onClick={() => setAttach(null)} className="text-red-400">✕</button></span>}
          </div>
        </Card>

        {/* Audience + actions */}
        <Card className="space-y-4 p-5" data-testid="audience-card">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Users2 className="h-5 w-5 text-primary" /> Destinatari</h2>
          <div className="space-y-2">
            {[["partners", "Partner"], ["clients", "Clienti"], ["agents", "Agenti"], ["prospects", "Prospect"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center justify-between text-sm">
                <span>{lbl}</span>
                <Switch checked={audience[k]} onCheckedChange={(v) => setAudience((a) => ({ ...a, [k]: v }))} data-testid={`aud-${k}`} />
              </label>
            ))}
          </div>
          {audience.prospects && regions.length > 0 && (
            <div className="space-y-1.5" data-testid="prospect-regions">
              <Label className="text-xs">Regioni Prospect (vuoto = tutte)</Label>
              <div className="flex flex-wrap gap-1.5">
                {regions.map((rg) => (
                  <button key={rg} data-testid={`prospect-region-${rg}`}
                    onClick={() => setAudience((a) => ({ ...a, prospect_regions: a.prospect_regions.includes(rg) ? a.prospect_regions.filter((x) => x !== rg) : [...a.prospect_regions, rg] }))}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${audience.prospect_regions.includes(rg) ? "border-primary bg-primary text-primary-foreground" : "border-input text-muted-foreground hover:border-primary/50"}`}>{rg}</button>
                ))}
              </div>
            </div>
          )}
          {categories.filter((c) => !c.locked).length > 0 && (
            <div className="space-y-1.5" data-testid="aud-categories">
              <Label className="text-xs">Rubriche / categorie</Label>
              <div className="flex flex-wrap gap-1.5">
                {categories.filter((c) => !c.locked).map((cat) => (
                  <button key={cat.slug} data-testid={`aud-cat-${cat.slug}`}
                    onClick={() => setAudience((a) => ({ ...a, contact_categories: a.contact_categories.includes(cat.slug) ? a.contact_categories.filter((x) => x !== cat.slug) : [...a.contact_categories, cat.slug] }))}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${audience.contact_categories.includes(cat.slug) ? "border-primary bg-primary text-primary-foreground" : "border-input text-muted-foreground hover:border-primary/50"}`}>{cat.name} ({cat.count})</button>
                ))}
              </div>
            </div>
          )}
          {audience.partners && (
            <div className="space-y-1.5" data-testid="cat-filter">
              <Label className="text-xs">Filtra Partner per categoria (vuoto = tutte)</Label>
              <div className="flex gap-2">
                {CATS.map((c) => (
                  <button key={c} onClick={() => toggleCat(c)} data-testid={`cat-${c}`}
                    className={`h-8 w-9 rounded-md border text-sm font-bold ${audience.partner_categories.includes(c) ? "border-primary bg-primary text-primary-foreground" : "border-input text-muted-foreground"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          {audience.partners && (
            <div className="space-y-1.5" data-testid="region-filter">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Filtra Partner per regione (vuoto = tutte)</Label>
                {audience.partner_regions.length > 0 && (
                  <button onClick={() => setAudience((a) => ({ ...a, partner_regions: [] }))} className="text-[11px] text-primary hover:underline" data-testid="clear-regions">Azzera</button>
                )}
              </div>
              <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-input bg-background/40 p-2">
                {regions.map((r) => (
                  <button key={r} onClick={() => toggleReg(r)} data-testid={`region-${r}`}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${audience.partner_regions.includes(r) ? "border-primary bg-primary text-primary-foreground" : "border-input text-muted-foreground hover:border-primary/50"}`}>{r}</button>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-md bg-primary/10 px-3 py-2 text-sm" data-testid="audience-count">
            Destinatari stimati: <b className="text-primary">{audienceOn ? (count ?? "…") : 0}</b>
            {audienceOn && audStats && audStats.duplicates > 0 && (
              <span className="ml-2 text-[11px] text-amber-400" data-testid="audience-dedup">({(audStats.total ?? 0).toLocaleString("it-IT")} totali − {(audStats.duplicates ?? 0).toLocaleString("it-IT")} doppioni = {(audStats.count ?? 0).toLocaleString("it-IT")} email uniche)</span>
            )}
          </div>
          {audienceOn && (count ?? 0) > 0 && (
            <button onClick={loadAudienceList} className="flex w-full items-center justify-center gap-2 rounded-md border border-input py-1.5 text-xs font-medium text-primary hover:bg-primary/5" data-testid="view-list-btn">
              {listBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Users2 className="h-3.5 w-3.5" />} {audList ? "Nascondi elenco" : `Vedi elenco (${count})`}
            </button>
          )}
          {audList && (
            <div className="max-h-64 overflow-y-auto rounded-md border border-white/10" data-testid="audience-list">
              {audList.length === 0 ? <p className="p-3 text-xs text-muted-foreground">Nessun destinatario.</p> : (
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-black/60 text-[10px] uppercase text-muted-foreground">
                    <tr><th className="px-2 py-1.5">Nome</th><th className="px-2 py-1.5">Email</th><th className="px-2 py-1.5">Tipo</th></tr>
                  </thead>
                  <tbody>
                    {audList.map((r, i) => (
                      <tr key={i} className="border-t border-white/5" data-testid={`aud-row-${i}`}>
                        <td className="max-w-[110px] truncate px-2 py-1">{r.name || "—"}</td>
                        <td className="max-w-[150px] truncate px-2 py-1 font-medium">{r.email}</td>
                        <td className="px-2 py-1 text-primary">{r.tipo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="space-y-2 border-t border-border pt-3">
            <div className="rounded-lg border border-input bg-muted/20 p-3" data-testid="rsvp-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary"><CalendarCheck className="h-4 w-4" /> Modulo evento / RSVP</div>
                <Switch checked={rsvp.enabled} onCheckedChange={(v) => setRsvp((s) => ({ ...s, enabled: v }))} data-testid="rsvp-enabled" />
              </div>
              {rsvp.enabled && (
                <div className="mt-3 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Collega a evento esistente (pagina Eventi Partner & Webinar)</Label>
                    <select value={rsvp.event_id || ""} onChange={(e) => setRsvp((s) => ({ ...s, event_id: e.target.value }))} data-testid="rsvp-event-select" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                      <option value="">— Nessun collegamento (evento inline) —</option>
                      {eventOpts.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}{ev.deadline_label ? ` · entro ${ev.deadline_label}` : ""}</option>)}
                    </select>
                    {rsvp.event_id && <p className="text-[11px] font-semibold text-emerald-500" data-testid="rsvp-event-linked">Titolo, data, luogo, chiusura iscrizioni e domande sono gestiti nella scheda dell'evento. La newsletter richiama solo le iscrizioni: non serve reinserire nulla qui.</p>}
                  </div>
                  {rsvp.event_id ? (() => {
                    const ev = eventOpts.find((e) => e.id === rsvp.event_id);
                    if (!ev) return null;
                    const dts = ev.dates || [];
                    return (
                      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs" data-testid="rsvp-event-recap">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-500">Dati presi dall'evento</div>
                        <div className="mt-1 text-sm font-bold text-foreground">{ev.title}</div>
                        {ev.location && <div className="mt-0.5 text-muted-foreground">Luogo: {ev.location}</div>}
                        {dts.length > 0 && <div className="mt-0.5 text-muted-foreground">Date: {dts.length === 1 ? (dts[0].label || dts[0].start) : `${dts.length} date disponibili`}</div>}
                        {ev.deadline_label && <div className="mt-0.5 text-muted-foreground">Chiusura iscrizioni: {ev.deadline_label}</div>}
                        <div className="mt-0.5 text-muted-foreground">Numero partecipanti: {ev.ask_guests === false ? "non richiesto" : "richiesto"}</div>
                      </div>
                    );
                  })() : (
                    <>
                      <div className="space-y-1.5"><Label className="text-xs">Titolo evento</Label><Input value={rsvp.title} onChange={(e) => setRsvp((s) => ({ ...s, title: e.target.value }))} placeholder="Es. Open Day Torri Solar" data-testid="rsvp-title" /></div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5"><Label className="text-xs">Data e ora</Label><Input type="datetime-local" value={rsvp.event_date} onChange={(e) => setRsvp((s) => ({ ...s, event_date: e.target.value }))} data-testid="rsvp-date" /></div>
                        <div className="space-y-1.5"><Label className="text-xs">Luogo</Label><Input value={rsvp.location} onChange={(e) => setRsvp((s) => ({ ...s, location: e.target.value }))} placeholder="Es. Sede Torri, Bergamo" data-testid="rsvp-location" /></div>
                      </div>
                      <div className="space-y-1.5"><Label className="text-xs">Descrizione breve</Label><Textarea value={rsvp.description} onChange={(e) => setRsvp((s) => ({ ...s, description: e.target.value }))} rows={2} placeholder="Dettagli dell'evento…" data-testid="rsvp-desc" /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Chiusura iscrizioni (facoltativa)</Label><Input type="datetime-local" value={rsvp.deadline} onChange={(e) => setRsvp((s) => ({ ...s, deadline: e.target.value }))} data-testid="rsvp-deadline" /><p className="text-[10px] text-muted-foreground">Dopo questa data i pulsanti mostrano "Iscrizioni chiuse".</p></div>
                      <label className="flex cursor-pointer items-center gap-2 text-xs"><Switch checked={rsvp.ask_guests} onCheckedChange={(v) => setRsvp((s) => ({ ...s, ask_guests: v }))} data-testid="rsvp-ask-guests" /> Chiedi il numero di partecipanti</label>
                    </>
                  )}
                  <p className="text-[10px] text-muted-foreground">In fondo all'email compaiono i pulsanti "Partecipo / Non partecipo". Le risposte confluiscono in "Eventi Partner &amp; Webinar" e nel dettaglio della campagna.</p>
                </div>
              )}
            </div>
            <div className="rounded-lg border border-input bg-muted/20 p-3" data-testid="poll-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary"><BarChart3 className="h-4 w-4" /> Sondaggio a un clic</div>
                <Switch checked={poll.enabled} onCheckedChange={(v) => setPoll((s) => ({ ...s, enabled: v }))} data-testid="poll-enabled" />
              </div>
              {poll.enabled && (
                <div className="mt-3 space-y-3">
                  <div className="space-y-1.5"><Label className="text-xs">Domanda</Label><Input value={poll.question} onChange={(e) => setPoll((s) => ({ ...s, question: e.target.value }))} placeholder="Es. Quale fascia oraria preferisci?" data-testid="poll-question" /></div>
                  <div className="space-y-2">
                    <Label className="text-xs">Opzioni (2-5)</Label>
                    {poll.options.map((o, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Input value={o} onChange={(e) => setPoll((s) => ({ ...s, options: s.options.map((x, j) => (j === i ? e.target.value : x)) }))} placeholder={`Opzione ${i + 1}`} data-testid={`poll-option-${i}`} />
                        {poll.options.length > 2 && <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => setPoll((s) => ({ ...s, options: s.options.filter((_, j) => j !== i) }))} data-testid={`poll-remove-${i}`}><XCircle className="h-4 w-4" /></Button>}
                      </div>
                    ))}
                    {poll.options.length < 5 && <Button type="button" variant="outline" size="sm" onClick={() => setPoll((s) => ({ ...s, options: [...s.options, ""] }))} data-testid="poll-add-option">+ Aggiungi opzione</Button>}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Ogni opzione è un pulsante: il destinatario vota con un clic dall'email e vede subito i risultati.</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => submit("draft")} disabled={!!busy} data-testid="save-draft-btn">{busy === "draft" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Bozza</Button>
              <Button variant="outline" size="sm" onClick={openPreview} disabled={previewing || !!busy} data-testid="preview-btn">{previewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Anteprima</Button>
              <Button variant="outline" size="sm" onClick={testSend} disabled={!!busy} data-testid="test-btn">{busy === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Prova</Button>
              <Button size="sm" onClick={() => submit("send")} disabled={!!busy} data-testid="send-now-btn">{busy === "send" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Invia ora</Button>
            </div>
            <div className="flex items-center gap-2">
              <Input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="h-9 text-xs" data-testid="sched-at" />
              <Button variant="outline" size="sm" onClick={() => submit("schedule")} disabled={!!busy} data-testid="schedule-btn"><Clock className="mr-2 h-4 w-4" /> Programma</Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Campaigns list */}
      <Card className="p-5" data-testid="campaigns-card">
        <h2 className="mb-3 font-heading text-lg font-bold">Campagne</h2>
        {campaigns.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="no-campaigns">Nessuna campagna ancora.</p> : (
          <div className="space-y-2" data-testid="campaigns-list">
            {campaigns.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`campaign-${c.id}`}>
                <div className={`min-w-0 ${c.status === "sent" ? "cursor-pointer" : ""}`} onClick={c.status === "sent" ? () => openDetail(c) : undefined} data-testid={c.status === "sent" ? `open-detail-${c.id}` : undefined}>
                  <div className="truncate text-sm font-medium">{c.subject || "(senza oggetto)"}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {STATUS_LABEL[c.status] || c.status}
                    {c.status === "scheduled" && c.scheduled_at ? ` · ${new Date(c.scheduled_at).toLocaleString("it-IT")}` : ""}
                    {c.status === "sent" && c.stats ? ` · ${c.stats.sent}/${c.stats.total} inviate${c.stats.failed ? ` · ${c.stats.failed} errori` : ""}` : ""}
                    {c.status === "sent" ? ` · ${c.opens || 0} aperture${c.stats?.sent ? ` (${Math.round((c.opens / c.stats.sent) * 100)}%)` : ""} · ${c.clicks || 0} clic${c.stats?.sent ? ` (${Math.round((c.clicks / c.stats.sent) * 100)}%)` : ""}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {c.status === "sent" && <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-primary" onClick={() => openDetail(c)} data-testid={`detail-btn-${c.id}`}><BarChart3 className="h-4 w-4" /> Dettaglio</Button>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.status === "sent" ? "bg-green-500/15 text-green-400" : c.status === "scheduled" ? "bg-primary/15 text-primary" : "bg-white/10 text-muted-foreground"}`}>{STATUS_LABEL[c.status] || c.status}</span>
                  {c.status !== "sent" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => editCampaign(c)} data-testid={`edit-campaign-${c.id}`}><Pencil className="h-4 w-4" /></Button>}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dupCampaign(c.id)} title="Duplica" data-testid={`dup-campaign-${c.id}`}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => delCampaign(c.id)} data-testid={`del-campaign-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Full email preview modal */}
      <Dialog open={preview !== null} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-5xl" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-primary" /> Anteprima email</DialogTitle>
          </DialogHeader>
          <div className="mb-1 flex w-fit items-center gap-1 rounded-md border border-input bg-muted/30 p-1">
            {[["both", "Entrambi", Layout], ["desktop", "Desktop", Monitor], ["mobile", "Telefono", Smartphone]].map(([m, lbl, Ico]) => (
              <button key={m} type="button" onClick={() => setPreviewMode(m)}
                className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors ${previewMode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`preview-mode-${m}`}>
                <Ico className="h-3.5 w-3.5" /> {lbl}
              </button>
            ))}
          </div>
          <div className="flex justify-center gap-6 overflow-auto">
            {previewMode !== "mobile" && (
              <div className="flex min-w-0 flex-1 flex-col items-center gap-2" data-testid="preview-desktop">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Monitor className="h-3.5 w-3.5" /> Desktop</span>
                <iframe title="anteprima-desktop" srcDoc={preview || ""} className="h-[68vh] w-full rounded-lg border border-white/10 bg-white" data-testid="preview-iframe" />
              </div>
            )}
            {previewMode !== "desktop" && (
              <div className="flex flex-col items-center gap-2" data-testid="preview-mobile">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Smartphone className="h-3.5 w-3.5" /> Telefono</span>
                <div className="rounded-[2rem] border-[7px] border-neutral-800 bg-neutral-800 shadow-xl">
                  <iframe title="anteprima-mobile" srcDoc={preview || ""} className="h-[68vh] w-[360px] rounded-[1.5rem] bg-white" data-testid="preview-iframe-mobile" />
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign detail modal */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl" data-testid="campaign-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Dettaglio campagna</DialogTitle>
          </DialogHeader>
          {detail && !detail.loading && !detailBusy && detail.failed > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
              <div className="text-xs text-red-300">{detail.failed} invii falliti (spesso rate-limit 429). Puoi reinviare solo a questi.</div>
              <Button size="sm" variant="outline" onClick={resendFailed} disabled={resending} className="border-red-500/40 text-red-300 hover:bg-red-500/20" data-testid="resend-failed-btn">
                {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Reinvia ai falliti
              </Button>
            </div>
          )}
          {detail?.loading || detailBusy ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Caricamento…</div>
          ) : detail ? (
            <div className="space-y-4">
              <div>
                <div className="text-sm font-semibold" data-testid="detail-subject">{detail.subject || "(senza oggetto)"}</div>
                <div className="text-[11px] text-muted-foreground">Inviata il {fmtDate(detail.sent_at)}</div>
              </div>
              {detail.poll && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="detail-poll">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary"><BarChart3 className="h-4 w-4" /> Sondaggio · {detail.poll.total} risposte</div>
                    {detail.poll.rows?.length > 0 && <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-primary" onClick={() => exportPoll(detailId)} data-testid="poll-export-btn"><Download className="h-3.5 w-3.5" /> Export CSV</Button>}
                  </div>
                  <div className="mb-2 text-sm font-semibold">{detail.poll.question}</div>
                  <div className="space-y-2">
                    {detail.poll.options.map((o, i) => {
                      const n = detail.poll.counts[i] || 0;
                      const pct = detail.poll.total ? Math.round((n / detail.poll.total) * 100) : 0;
                      return (
                        <div key={i} data-testid={`poll-result-${i}`}>
                          <div className="flex justify-between text-xs"><span>{o}</span><span className="text-muted-foreground">{pct}% ({n})</span></div>
                          <div className="mt-1 h-2.5 rounded-full bg-muted"><div className="h-2.5 rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                  {detail.poll.rows?.length > 0 && (
                    <div className="mt-3 border-t border-primary/20 pt-2" data-testid="poll-voters">
                      <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Chi ha votato ({detail.poll.rows.length})</div>
                      <div className="max-h-48 space-y-1 overflow-auto">
                        {detail.poll.rows.map((r, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`poll-voter-${i}`}>
                            <span className="truncate">{r.name || r.email}</span>
                            <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">{r.option_label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {detail.rsvp && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="detail-rsvp">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary"><CalendarCheck className="h-4 w-4" /> RSVP evento</div>
                    <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-primary" onClick={() => exportRsvp(detailId)} data-testid="rsvp-export-btn"><Download className="h-3.5 w-3.5" /> Export CSV</Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-md border border-white/10 bg-black/30 p-2"><div className="text-lg font-black text-green-500" data-testid="rsvp-si">{detail.rsvp.si}</div><div className="text-[10px] uppercase text-muted-foreground">Partecipano</div></div>
                    <div className="rounded-md border border-white/10 bg-black/30 p-2"><div className="text-lg font-black" data-testid="rsvp-guests">{detail.rsvp.guests}</div><div className="text-[10px] uppercase text-muted-foreground">Persone totali</div></div>
                    <div className="rounded-md border border-white/10 bg-black/30 p-2"><div className="text-lg font-black text-red-400" data-testid="rsvp-no">{detail.rsvp.no}</div><div className="text-[10px] uppercase text-muted-foreground">Non partecipano</div></div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center" data-testid="stat-total">
                  <Mail className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                  <div className="text-lg font-black">{detail.total}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Destinatari</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center" data-testid="stat-accepted">
                  <Send className="mx-auto mb-1 h-4 w-4 text-sky-400" />
                  <div className="text-lg font-black text-sky-400">{detail.accepted}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Accettate</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center" data-testid="stat-failed">
                  <XCircle className="mx-auto mb-1 h-4 w-4 text-red-400" />
                  <div className="text-lg font-black text-red-400">{detail.failed}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Errori</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center" data-testid="stat-opened">
                  <Eye className="mx-auto mb-1 h-4 w-4 text-green-400" />
                  <div className="text-lg font-black text-green-400">{detail.opened}{detail.accepted ? <span className="text-xs font-semibold"> · {Math.round((detail.opened / detail.accepted) * 100)}%</span> : null}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Aperture uniche</div>
                  <div className="text-[10px] text-muted-foreground">{detail.total_opens} totali</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-center" data-testid="stat-clicked">
                  <MousePointerClick className="mx-auto mb-1 h-4 w-4 text-primary" />
                  <div className="text-lg font-black text-primary">{detail.clicked}{detail.accepted ? <span className="text-xs font-semibold"> · {Math.round((detail.clicked / detail.accepted) * 100)}%</span> : null}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Clic unici</div>
                  <div className="text-[10px] text-muted-foreground">{detail.total_clicks} totali</div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground">Aperture uniche/clic unici = destinatari distinti; "totali" include le ripetizioni. Nota: il conteggio aperture via pixel può essere sovrastimato (pre-caricamento immagini di alcuni client).</p>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-white/10" data-testid="detail-recipients">
                {detail.recipients.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">Nessun destinatario registrato.</p>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-black/60 text-[10px] uppercase text-muted-foreground">
                      <tr><th className="px-3 py-2">Email</th><th className="px-3 py-2">Stato</th><th className="px-3 py-2">Aperto</th><th className="px-3 py-2">Cliccato</th></tr>
                    </thead>
                    <tbody>
                      {detail.recipients.map((r, i) => (
                        <tr key={i} className="border-t border-white/5" data-testid={`recipient-row-${i}`}>
                          <td className="max-w-[200px] truncate px-3 py-1.5 font-medium">{r.email}</td>
                          <td className="px-3 py-1.5">
                            {r.status === "failed"
                              ? <span className="text-red-400 cursor-help" title={r.error || "Errore sconosciuto"} data-testid={`recipient-error-${i}`}>Errore{r.error ? <span className="ml-1 text-[10px] text-red-400/70">· {String(r.error).slice(0, 60)}</span> : null}</span>
                              : <span className="text-muted-foreground">Inviata</span>}
                          </td>
                          <td className="px-3 py-1.5">
                            {r.opened_at || r.clicked_at ? <span className="text-green-400" title={fmtDate(r.opened_at || r.clicked_at)}>✅ {fmtDate(r.opened_at || r.clicked_at)}{r.open_count > 1 ? ` ·×${r.open_count}` : ""}</span> : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-1.5">
                            {r.clicked_at ? <span className="text-primary" title={fmtDate(r.clicked_at)}>🔗 {fmtDate(r.clicked_at)}{r.click_count > 1 ? ` ·×${r.click_count}` : ""}</span> : <span className="text-muted-foreground">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
